import { useState, useRef, useCallback, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Sparkle, Lightbulb, Buildings, PaperPlaneRight, Cube, ThumbsUp, ThumbsDown } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { API_URL } from 'constants/index';
import { ScenarioManager } from '../helpdesk/components/AiAssistant/ScenarioManager';
import { ScenarioResult, AssistantContext } from '../helpdesk/components/AiAssistant/types';
import { AnalyticsVisualization } from '../helpdesk/components/AnalyticsVisualization';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import EllaAvatar from 'assets/ella.png';

interface Message {
  id: string;
  role: 'user' | 'ella';
  content: string;
  timestamp: Date;
  chartData?: any;
  result?: ScenarioResult;
  isProcessing?: boolean;
  originalInput?: string; // Store original user input for execute actions
  backendMessageId?: number; // Backend message ID for feedback tracking
  feedbackRating?: 'thumbs_up' | 'thumbs_down'; // User's feedback rating
}

/**
 * Ella Chat Page
 *
 * Een volledig conversatie-gebaseerde interface zoals ChatGPT/Claude
 * waar je continu kunt doorpraten met Ella, je AI assistent.
 *
 * Features:
 * - Message history met scrollable chat interface
 * - Ella avatar branding
 * - Analytics visualisatie inline in berichten
 * - @-mention functionaliteit voor gebouw suggesties
 * - Scenario detectie (tickets, analytics, etc.)
 */
export const EllaChat: React.FC = () => {
  const { session } = useSession();
  const location = useLocation();
  const scenarioManager = useRef(new ScenarioManager()).current;

  // State
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'ella',
      content: 'Hallo! Ik ben Ella, je AI assistent. Ik kan je helpen met analytics, tickets aanmaken, en veel meer. Waar kan ik je mee helpen?',
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const initialQuestionProcessed = useRef(false);

  // Conversation tracking for database persistence
  const [conversationId, setConversationId] = useState<number | null>(null);
  const conversationStartTimeRef = useRef<number | null>(null);

  // Context tracking for follow-up questions
  const [lastAnalyzedContext, setLastAnalyzedContext] = useState<{
    type: 'document' | 'ticket' | 'analytics';
    documentId?: number;
    ticketId?: number;
    buildingId?: string;
    unitId?: string;
    label?: string;
    timestamp: Date;
  } | null>(null);

  // @-mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState<Array<{
    id: string;
    type: 'building' | 'unit';
    googlePlaceLongAddress?: string;
    companyName?: string;
    boxNumber?: string;
    buildingId?: string;
    matchScore: number;
  }>>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [lastSelectedBuilding, setLastSelectedBuilding] = useState<string | null>(null);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Auto-scroll naar beneden bij nieuwe berichten
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Initialize conversation session when component mounts
   */
  useEffect(() => {
    const initConversation = async () => {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/kaylee/conversation`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              clerkUserId: session.user.id,
              userEmail: session.user.primaryEmailAddress?.emailAddress,
              conversationType: 'CHAT',
            }),
          },
          session
        );

        if (response.ok) {
          const data = await response.json();
          setConversationId(data.conversation.id);
          console.log('💬 Ella conversation initialized:', data.conversation.id);
        }
      } catch (error) {
        console.error('Failed to initialize conversation:', error);
      }
    };

    initConversation();
  }, [session]);

  /**
   * Log interaction to backend for analytics and feedback
   */
  const logInteractionToBackend = useCallback(async (
    userPrompt: string,
    aiResponse: string,
    metadata?: {
      intent?: string;
      detectedLabel?: string;
      detectedBuildingId?: string;
      detectedUnitId?: string;
    }
  ): Promise<number | undefined> => {
    if (!conversationId || !session) return undefined;

    const responseTimeMs = conversationStartTimeRef.current
      ? Date.now() - conversationStartTimeRef.current
      : undefined;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/kaylee/conversation/${conversationId}/interaction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userPrompt,
            aiResponse,
            intent: metadata?.intent,
            detectedLabel: metadata?.detectedLabel,
            detectedBuildingId: metadata?.detectedBuildingId,
            detectedUnitId: metadata?.detectedUnitId,
            responseTimeMs,
          }),
        },
        session
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Logged interaction to backend:', data.assistantMessageId);
        return data.assistantMessageId;
      }
    } catch (error) {
      console.error('Failed to log interaction:', error);
    }

    return undefined;
  }, [conversationId, session]);

  /**
   * Handle initial question from navigation state (Quick Ella prompt)
   */
  useEffect(() => {
    const initialQuestion = (location.state as any)?.initialQuestion;

    if (initialQuestion && !initialQuestionProcessed.current && !isProcessing && session) {
      initialQuestionProcessed.current = true;
      setInput(initialQuestion);

      // Auto-submit after a short delay to ensure UI is ready
      setTimeout(() => {
        // Manually trigger send message logic
        if (!input.trim() && initialQuestion.trim()) {
          // We need to send the initialQuestion directly since input might not be updated yet
          const sendInitialMessage = async () => {
            const userMessage: Message = {
              id: Date.now().toString(),
              role: 'user',
              content: initialQuestion.trim(),
              timestamp: new Date(),
            };

            setMessages(prev => [...prev, userMessage]);
            setInput('');
            setIsProcessing(true);

            const ellaMessageId = `ella-${Date.now()}`;
            const placeholderEllaMessage: Message = {
              id: ellaMessageId,
              role: 'ella',
              content: 'Een moment, ik ben je vraag aan het verwerken...',
              timestamp: new Date(),
              isProcessing: true,
            };
            setMessages(prev => [...prev, placeholderEllaMessage]);

            try {
              const { handler, detection } = scenarioManager.detectScenario(initialQuestion.trim());

              if (detection.confidence < 0.3) {
                setMessages(prev => prev.map(msg =>
                  msg.id === ellaMessageId
                    ? {
                        ...msg,
                        content: 'Ik begrijp je vraag niet helemaal. Probeer een voorbeeld query of vraag me wat ik allemaal kan doen!',
                        isProcessing: false,
                      }
                    : msg
                ));
                setIsProcessing(false);
                return;
              }

              const context: AssistantContext = {
                session,
                apiUrl: API_URL,
                onError: (error) => {
                  console.error('Error:', error);
                  toast.error(error.message);
                }
              };

              const parsedResult = await handler.parse(initialQuestion.trim(), context);

              if (parsedResult.action === 'analyze' || parsedResult.action === 'query') {
                const data = await handler.execute(parsedResult, context);
                setMessages(prev => prev.map(msg =>
                  msg.id === ellaMessageId
                    ? {
                        ...msg,
                        content: `Hier zijn de resultaten voor je vraag over ${parsedResult.scenario}:`,
                        chartData: data,
                        result: parsedResult,
                        originalInput: initialQuestion.trim(),
                        isProcessing: false,
                      }
                    : msg
                ));
              } else if (parsedResult.action === 'create' && parsedResult.preview) {
                setMessages(prev => prev.map(msg =>
                  msg.id === ellaMessageId
                    ? {
                        ...msg,
                        content: `Ik heb de volgende ${parsedResult.scenario} voorbereid. Klik op "Uitvoeren" om te bevestigen.`,
                        result: parsedResult,
                        originalInput: initialQuestion.trim(),
                        isProcessing: false,
                      }
                    : msg
                ));
              }
            } catch (error: any) {
              console.error('Error processing input:', error);
              setMessages(prev => prev.map(msg =>
                msg.id === ellaMessageId
                  ? {
                      ...msg,
                      content: `Sorry, er ging iets mis bij het verwerken van je vraag: ${error.message || 'Onbekende fout'}`,
                      isProcessing: false,
                    }
                  : msg
              ));
            } finally {
              setIsProcessing(false);
            }
          };

          sendInitialMessage();
        }
      }, 500);
    }
  }, [location.state, session, scenarioManager]);

  /**
   * Fetch buildings and units based on search query
   * If lastSelectedBuilding is set, filter units by that building
   */
  const searchBuildingsAndUnits = useCallback(async (query: string) => {
    console.log('[Mentions] searchBuildingsAndUnits called with query:', query);
    if (!session) {
      console.log('[Mentions] No session available, skipping search');
      return;
    }

    try {
      // Search both buildings and units in parallel
      const buildingsUrl = `${API_URL}/helpdesk/buildings/fuzzy-search?q=${encodeURIComponent(query)}`;
      const unitsUrl = `${API_URL}/units/fuzzy-search?q=${encodeURIComponent(query)}${lastSelectedBuilding ? `&buildingId=${lastSelectedBuilding}` : ''}`;

      const [buildingsResponse, unitsResponse] = await Promise.all([
        fetchWithAuth(buildingsUrl, { method: 'GET' }, session),
        fetchWithAuth(unitsUrl, { method: 'GET' }, session)
      ]);

      const buildings = buildingsResponse.ok ? await buildingsResponse.json() : [];
      const units = unitsResponse.ok ? await unitsResponse.json() : [];

      console.log('[Mentions] Buildings received:', buildings.length);
      console.log('[Mentions] Units received:', units.length);

      // Tag each result with its type
      const buildingsWithType = buildings.map((b: any) => ({ ...b, type: 'building' }));
      const unitsWithType = units.map((u: any) => ({ ...u, type: 'unit' }));

      // Combine and sort by match score
      const combined = [...buildingsWithType, ...unitsWithType]
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, 8);

      setMentionSuggestions(combined);
    } catch (error) {
      console.error('[Mentions] Error searching buildings and units:', error);
    }
  }, [session, lastSelectedBuilding]);

  /**
   * Detecteer @ in input voor autocomplete
   */
  const checkForMentions = useCallback((text: string, cursor: number) => {
    console.log('[Mentions] Checking for mentions:', { text, cursor });
    const textBeforeCursor = text.substring(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    console.log('[Mentions] Last @ index:', lastAtIndex, 'Text before cursor:', textBeforeCursor);

    if (lastAtIndex >= 0) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      console.log('[Mentions] Text after @:', textAfterAt);
      if (!textAfterAt.includes(' ')) {
        const query = textAfterAt;
        console.log('[Mentions] Opening dropdown with query:', query);
        setMentionQuery(query);
        setMentionStartPos(lastAtIndex);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0);

        if (query.length > 0) {
          console.log('[Mentions] Fetching buildings and units with query:', query);
          searchBuildingsAndUnits(query);
        } else {
          console.log('[Mentions] Fetching all buildings and units');
          searchBuildingsAndUnits('');
        }
        return;
      }
    }

    console.log('[Mentions] Closing dropdown');
    setShowMentionDropdown(false);
    setMentionQuery('');
  }, [searchBuildingsAndUnits]);

  /**
   * Handle building/unit selection via @-mention
   */
  const selectMention = useCallback((item: { id: string; type: 'building' | 'unit'; name?: string }) => {
    const beforeMention = input.substring(0, mentionStartPos);
    const afterMention = input.substring(mentionStartPos + mentionQuery.length + 1);
    const newText = `${beforeMention}@${item.id} ${afterMention}`;

    setInput(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');

    // Track last selected building for context-aware unit search
    if (item.type === 'building') {
      setLastSelectedBuilding(item.id);
    }

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = beforeMention.length + item.id.length + 2;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [input, mentionStartPos, mentionQuery]);

  /**
   * Handle keyboard navigation in mention dropdown
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Handle Enter key for sending message
    if (e.key === 'Enter' && !e.shiftKey && !showMentionDropdown) {
      e.preventDefault();
      handleSendMessage();
      return;
    }

    // Handle mention dropdown navigation
    if (!showMentionDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev =>
        prev < mentionSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter' && mentionSuggestions.length > 0) {
      e.preventDefault();
      selectMention(mentionSuggestions[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
      setMentionQuery('');
    }
  }, [showMentionDropdown, mentionSuggestions, selectedMentionIndex, selectMention]);

  /**
   * Send user message and process with Ella
   */
  const handleSendMessage = async () => {
    if (!input.trim() || isProcessing) {
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    // Voeg user message toe
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input.trim();
    setInput('');
    setIsProcessing(true);

    // Track response time for analytics
    conversationStartTimeRef.current = Date.now();

    // Voeg placeholder Ella message toe (met loading state)
    const ellaMessageId = `ella-${Date.now()}`;
    const placeholderEllaMessage: Message = {
      id: ellaMessageId,
      role: 'ella',
      content: 'Een moment, ik ben je vraag aan het verwerken...',
      timestamp: new Date(),
      isProcessing: true,
    };
    setMessages(prev => [...prev, placeholderEllaMessage]);

    try {
      // Detect scenario
      const { handler, detection } = scenarioManager.detectScenario(currentInput);

      // Check if this is a follow-up question to previous context
      const isLikelyFollowUp = detection.confidence < 0.3 && lastAnalyzedContext &&
        (new Date().getTime() - lastAnalyzedContext.timestamp.getTime()) < 5 * 60 * 1000; // Within 5 minutes

      if (isLikelyFollowUp && lastAnalyzedContext.type === 'document' && lastAnalyzedContext.documentId) {
        // This is a follow-up question about the same document
        console.log('🔄 Detected follow-up question about document', lastAnalyzedContext.documentId);
        console.log('📝 Follow-up question:', currentInput);

        try {
          const response = await fetchWithAuth(
            `${API_URL}/file/ai/analyze-document/${lastAnalyzedContext.documentId}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                question: currentInput,
                sessionId: `ella-followup-${Date.now()}`,
              }),
            },
            session!
          );

          if (!response.ok) {
            throw new Error('Document analysis failed');
          }

          const data = await response.json();

          // Log interaction to backend and get message ID
          const backendMessageId = await logInteractionToBackend(
            currentInput,
            data.analysis,
            {
              intent: 'DOCUMENT_ANALYSIS',
              detectedLabel: lastAnalyzedContext.label,
              detectedBuildingId: lastAnalyzedContext.buildingId,
              detectedUnitId: lastAnalyzedContext.unitId,
            }
          );

          setMessages(prev => prev.map(msg =>
            msg.id === ellaMessageId
              ? {
                  ...msg,
                  content: data.analysis,
                  isProcessing: false,
                  backendMessageId,
                }
              : msg
          ));
          setIsProcessing(false);
          return;
        } catch (error: any) {
          console.error('Error in follow-up document analysis:', error);
          toast.error('Kon vervolgvraag niet verwerken');
          // Fall through to regular error handling
        }
      }

      if (detection.confidence < 0.3) {
        // Update Ella message met foutmelding
        setMessages(prev => prev.map(msg =>
          msg.id === ellaMessageId
            ? {
                ...msg,
                content: isLikelyFollowUp
                  ? 'Sorry, ik kon je vervolgvraag niet verwerken. Probeer specifieker te zijn of herhaal wat je wilt weten.'
                  : 'Ik begrijp je vraag niet helemaal. Probeer een voorbeeld query of vraag me wat ik allemaal kan doen!',
                isProcessing: false,
              }
            : msg
        ));
        setIsProcessing(false);
        return;
      }

      const context: AssistantContext = {
        session,
        apiUrl: API_URL,
        onError: (error) => {
          console.error('Error:', error);
          toast.error(error.message);
        }
      };

      // Parse input
      const parsedResult = await handler.parse(currentInput, context);

      // Voor analytics/query: direct uitvoeren
      if (parsedResult.action === 'analyze' || parsedResult.action === 'query') {
        const data = await handler.execute(parsedResult, context);
        console.log('Received data from handler:', data);

        // Check if response is a text-response (for document analysis)
        if (data && data.type === 'text-response') {
          // Log interaction to backend
          const backendMessageId = await logInteractionToBackend(
            currentInput,
            data.message,
            { intent: 'DOCUMENT_ANALYSIS' }
          );

          setMessages(prev => prev.map(msg =>
            msg.id === ellaMessageId
              ? {
                  ...msg,
                  content: data.message,
                  isProcessing: false,
                  backendMessageId,
                }
              : msg
          ));
        }
        // Check if this is a validity-check with a fileLog - save context for follow-ups
        else if (data && data.type === 'validity-check' && data.fileLog) {
          console.log('💾 Saving document context for follow-up questions:', data.fileLog.id);
          setLastAnalyzedContext({
            type: 'document',
            documentId: data.fileLog.id,
            buildingId: parsedResult.data?.buildingId,
            unitId: parsedResult.data?.unitId,
            label: parsedResult.data?.label,
            timestamp: new Date(),
          });

          const responseMessage = `Hier zijn de resultaten voor je vraag over ${parsedResult.scenario}:`;

          // Log interaction to backend
          const backendMessageId = await logInteractionToBackend(
            currentInput,
            responseMessage,
            {
              intent: 'DOCUMENT_ANALYSIS',
              detectedLabel: parsedResult.data?.label,
              detectedBuildingId: parsedResult.data?.buildingId,
              detectedUnitId: parsedResult.data?.unitId,
            }
          );

          // Regular analytics data with visualization
          setMessages(prev => prev.map(msg =>
            msg.id === ellaMessageId
              ? {
                  ...msg,
                  content: responseMessage,
                  chartData: data,
                  result: parsedResult,
                  originalInput: currentInput,
                  isProcessing: false,
                  backendMessageId,
                }
              : msg
          ));
        }
        else {
          const responseMessage = `Hier zijn de resultaten voor je vraag over ${parsedResult.scenario}:`;

          // Log interaction to backend
          const backendMessageId = await logInteractionToBackend(
            currentInput,
            responseMessage,
            {
              intent: parsedResult.scenario?.includes('ticket') ? 'TICKET_QUERY' : 'ANALYTICS',
              detectedLabel: parsedResult.data?.label,
              detectedBuildingId: parsedResult.data?.buildingId,
              detectedUnitId: parsedResult.data?.unitId,
            }
          );

          // Regular analytics data with visualization
          setMessages(prev => prev.map(msg =>
            msg.id === ellaMessageId
              ? {
                  ...msg,
                  content: responseMessage,
                  chartData: data,
                  result: parsedResult,
                  originalInput: currentInput,
                  isProcessing: false,
                  backendMessageId,
                }
              : msg
          ));
        }
      }
      // Voor create: toon preview
      else if (parsedResult.action === 'create' && parsedResult.preview) {
        setMessages(prev => prev.map(msg =>
          msg.id === ellaMessageId
            ? {
                ...msg,
                content: `Ik heb de volgende ${parsedResult.scenario} voorbereid. Klik op "Uitvoeren" om te bevestigen.`,
                result: parsedResult,
                originalInput: currentInput,
                isProcessing: false,
              }
            : msg
        ));
      }
    } catch (error: any) {
      console.error('Error processing input:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === ellaMessageId
          ? {
              ...msg,
              content: `Sorry, er ging iets mis bij het verwerken van je vraag: ${error.message || 'Onbekende fout'}`,
              isProcessing: false,
            }
          : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Execute een create actie (na preview bevestiging)
   */
  const handleExecuteAction = async (messageId: string, result: ScenarioResult) => {
    setIsProcessing(true);

    try {
      // Find the message to get the original input
      const message = messages.find(msg => msg.id === messageId);

      if (!message || !message.originalInput) {
        throw new Error('Kan oorspronkelijke vraag niet vinden');
      }

      // Re-detect scenario using the stored original input
      const { handler } = scenarioManager.detectScenario(message.originalInput);

      if (!handler) {
        throw new Error('Kan scenario handler niet vinden');
      }

      // Update message met success
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
              ...msg,
              content: `✅ ${result.scenario} succesvol aangemaakt!`,
              result: undefined, // Verwijder preview
            }
          : msg
      ));
    } catch (error: any) {
      console.error('Error executing action:', error);
      toast.error(error.message || 'Fout bij uitvoeren van actie');

      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
              ...msg,
              content: `❌ Fout bij uitvoeren: ${error.message || 'Onbekende fout'}`,
            }
          : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Analyze specific ticket using AI chat endpoint
   * Adds the analysis as a new Ella message in the conversation
   */
  const handleAnalyzeTicket = useCallback(async (ticketId: number) => {
    if (!session) {
      toast.error('Geen sessie beschikbaar');
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: `Analyseer ticket H${ticketId} uitgebreid`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Add placeholder Ella message
    const ellaMessageId = `ella-${Date.now()}`;
    const placeholderEllaMessage: Message = {
      id: ellaMessageId,
      role: 'ella',
      content: `Ik ben ticket H${ticketId} aan het analyseren met alle beschikbare data...`,
      timestamp: new Date(),
      isProcessing: true,
    };
    setMessages(prev => [...prev, placeholderEllaMessage]);
    setIsProcessing(true);

    try {
      console.log(`🤖 Ella: Calling AI chat endpoint for ticket H${ticketId}`);

      const question = `Analyseer dit ticket uitgebreid. Wat is het probleem? Welke acties zijn ondernomen? Wat zijn de next steps?`;

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ai/chat-ticket/${ticketId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ question }),
        },
        session
      );

      if (!response.ok) {
        throw new Error('AI chat request failed');
      }

      const data = await response.json();
      console.log('🎉 Ella: AI chat response:', data);

      // Update Ella message with the AI analysis
      setMessages(prev => prev.map(msg =>
        msg.id === ellaMessageId
          ? {
              ...msg,
              content: data.answer,
              isProcessing: false,
            }
          : msg
      ));
    } catch (error: any) {
      console.error('❌ Ella: Error calling AI chat:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === ellaMessageId
          ? {
              ...msg,
              content: `Sorry, er ging iets mis bij het analyseren van ticket H${ticketId}: ${error.message || 'Onbekende fout'}`,
              isProcessing: false,
            }
          : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  }, [session]);

  /**
   * Handle drill-down into underlying tickets when clicking on aggregate data
   */
  const handleDrillDown = useCallback(async (filters: { label?: string; buildingId?: string; unitId?: string }) => {
    if (isProcessing) return;

    // Generate natural language query based on filters
    let query = 'Toon tickets';
    if (filters.label) {
      query = `Toon alle ${filters.label} tickets`;
    } else if (filters.buildingId) {
      query = `Toon alle tickets voor @${filters.buildingId}`;
    } else if (filters.unitId) {
      query = `Toon alle tickets voor unit @${filters.unitId}`;
    }

    // Create user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: query,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsProcessing(true);

    // Create placeholder Ella message
    const ellaMessageId = `ella-${Date.now()}`;
    const placeholderEllaMessage: Message = {
      id: ellaMessageId,
      role: 'ella',
      content: 'Een moment, ik haal de tickets op...',
      timestamp: new Date(),
      isProcessing: true,
    };
    setMessages(prev => [...prev, placeholderEllaMessage]);

    try {
      // Detect scenario
      const { handler, detection } = scenarioManager.detectScenario(query);

      if (detection.confidence < 0.3) {
        setMessages(prev => prev.map(msg =>
          msg.id === ellaMessageId
            ? {
                ...msg,
                content: 'Ik begrijp je vraag niet helemaal. Probeer het opnieuw!',
                isProcessing: false,
              }
            : msg
        ));
        setIsProcessing(false);
        return;
      }

      const context: AssistantContext = {
        session,
        apiUrl: API_URL,
        onError: (error) => {
          console.error('Error:', error);
          toast.error(error.message);
        }
      };

      // Parse and execute
      const parsedResult = await handler.parse(query, context);
      const data = await handler.execute(parsedResult, context);

      // Update Ella message with results
      setMessages(prev => prev.map(msg =>
        msg.id === ellaMessageId
          ? {
              ...msg,
              content: `Hier zijn de tickets${filters.label ? ` voor ${filters.label}` : filters.buildingId ? ` voor ${filters.buildingId}` : ''}:`,
              chartData: data,
              result: parsedResult,
              originalInput: query,
              isProcessing: false,
            }
          : msg
      ));
    } catch (error: any) {
      console.error('Error during drill-down:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === ellaMessageId
          ? {
              ...msg,
              content: `Sorry, er ging iets mis: ${error.message || 'Onbekende fout'}`,
              isProcessing: false,
            }
          : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  }, [session, isProcessing, scenarioManager]);

  /**
   * Handle document analysis request - submits query to Ella chat
   */
  const handleAnalyzeDocument = useCallback((documentId: number, documentLabel?: string[]) => {
    console.log('🤖 Ella: Analyzing document inline', documentId);

    // Build question based on document label if available
    const labelInfo = documentLabel && documentLabel.length > 0 ? documentLabel.join(', ') : '';
    const question = labelInfo
      ? `Analyseer document ${documentId} - Wat is het label van dit ${labelInfo}?`
      : `Analyseer document ${documentId} - Wat staat er in dit document?`;

    // Set the input to trigger analysis
    setInput(question);

    // Auto-submit after a short delay
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  }, []);

  /**
   * Submit feedback for a message
   */
  const handleSubmitFeedback = useCallback(async (
    messageId: string,
    backendMessageId: number | undefined,
    rating: 'thumbs_up' | 'thumbs_down'
  ) => {
    if (!session) {
      toast.error('Geen sessie beschikbaar');
      return;
    }

    // For now, if there's no backend message ID, we'll create a placeholder
    // In the future, all Ella responses should have a backend message ID from the logging system
    if (!backendMessageId) {
      console.warn('No backend message ID available for feedback. Skipping backend submission.');

      // Still update UI to show feedback was given
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, feedbackRating: rating }
          : msg
      ));

      toast.info(rating === 'thumbs_up' ? 'Bedankt voor je positieve feedback!' : 'Bedankt voor je feedback! We zullen dit verbeteren.');
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/kaylee/feedback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messageId: backendMessageId,
            clerkUserId: session.user.id,
            rating: rating,
          }),
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      // Update message with feedback rating
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, feedbackRating: rating }
          : msg
      ));

      toast.success(
        rating === 'thumbs_up'
          ? 'Bedankt voor je positieve feedback!'
          : 'Bedankt voor je feedback! We zullen dit verbeteren.'
      );
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast.error('Kon feedback niet versturen');
    }
  }, [session]);

  /**
   * Voorbeeld queries
   */
  const examplesByScenario = scenarioManager.getAllExamples();

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-moss-primary/5 to-brick-primary/5">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-neutral-primary/10 shadow-sm">
        <div className="flex items-center gap-3">
          <img src={EllaAvatar} alt="Ella" className="w-10 h-10 rounded-full" />
          <div>
            <h1 className="text-xl font-semibold text-neutral-primary">Ella</h1>
            <p className="text-xs text-neutral-secondaryMid">Je AI assistent</p>
          </div>
        </div>
        <Sparkle className="w-6 h-6 text-brick-primary" weight="fill" />
      </div>

      {/* Chat Messages Container */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-6"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {/* Ella Avatar */}
            {message.role === 'ella' && (
              <img
                src={EllaAvatar}
                alt="Ella"
                className="w-8 h-8 rounded-full flex-shrink-0 mt-1"
              />
            )}

            {/* Message Content */}
            <div
              className={`max-w-2xl ${
                message.role === 'user'
                  ? 'bg-moss-primary text-white rounded-2xl rounded-tr-sm px-4 py-3'
                  : 'bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-neutral-primary/10'
              }`}
            >
              {/* Text Content */}
              <p className={`text-sm whitespace-pre-wrap ${
                message.role === 'user' ? 'text-white' : 'text-neutral-primary'
              }`}>
                {message.content}
              </p>

              {/* Chart Data */}
              {message.chartData && (
                <div className="mt-4">
                  <AnalyticsVisualization
                    analyticsData={message.chartData}
                    onAnalyzeTicket={handleAnalyzeTicket}
                    onAnalyzeDocument={handleAnalyzeDocument}
                    onDrillDown={handleDrillDown}
                  />
                </div>
              )}

              {/* Preview/Create Actions */}
              {message.result && message.result.action === 'create' && message.result.preview && (
                <div className="mt-4 border-t border-neutral-primary/10 pt-3 space-y-3">
                  <div className="text-xs font-semibold text-moss-primary">
                    Preview - {message.result.scenario}
                  </div>
                  <div className="space-y-2 text-xs">
                    {Object.entries(message.result.preview).map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="font-medium text-neutral-secondaryMid min-w-[100px] capitalize">
                          {key}:
                        </span>
                        <span className="text-neutral-primary">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => handleExecuteAction(message.id, message.result!)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 text-xs bg-moss-primary text-white rounded-md hover:bg-moss-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isProcessing ? 'Uitvoeren...' : 'Uitvoeren'}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Timestamp */}
              <div className={`text-xs mt-2 ${
                message.role === 'user' ? 'text-white/70' : 'text-neutral-secondaryMid'
              }`}>
                {message.timestamp.toLocaleTimeString('nl-BE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>

              {/* Feedback buttons - only for Ella messages and not processing */}
              {message.role === 'ella' && !message.isProcessing && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-primary/10">
                  <span className="text-xs text-neutral-secondaryMid">Was dit antwoord nuttig?</span>
                  <button
                    onClick={() => handleSubmitFeedback(message.id, message.backendMessageId, 'thumbs_up')}
                    disabled={message.feedbackRating !== undefined}
                    className={`p-1 rounded transition-colors ${
                      message.feedbackRating === 'thumbs_up'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-moss-primary/10 text-neutral-secondaryMid hover:text-moss-primary'
                    } disabled:cursor-not-allowed`}
                    title="Duimen omhoog"
                  >
                    <ThumbsUp
                      className="w-4 h-4"
                      weight={message.feedbackRating === 'thumbs_up' ? 'fill' : 'regular'}
                    />
                  </button>
                  <button
                    onClick={() => handleSubmitFeedback(message.id, message.backendMessageId, 'thumbs_down')}
                    disabled={message.feedbackRating !== undefined}
                    className={`p-1 rounded transition-colors ${
                      message.feedbackRating === 'thumbs_down'
                        ? 'bg-red-100 text-red-600'
                        : 'hover:bg-red-50 text-neutral-secondaryMid hover:text-red-600'
                    } disabled:cursor-not-allowed`}
                    title="Duimen omlaag"
                  >
                    <ThumbsDown
                      className="w-4 h-4"
                      weight={message.feedbackRating === 'thumbs_down' ? 'fill' : 'regular'}
                    />
                  </button>
                  {message.feedbackRating && (
                    <span className="text-xs text-neutral-secondaryMid italic ml-1">
                      Bedankt voor je feedback!
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* User Icon Placeholder */}
            {message.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-neutral-primary flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-xs font-semibold">JIJ</span>
              </div>
            )}
          </div>
        ))}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Example Queries (show when no messages yet) */}
      {messages.length === 1 && (
        <div className="px-4 pb-4">
          <div className="bg-white rounded-lg border border-blue-200 p-4 max-w-2xl mx-auto">
            <div className="flex items-start gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <strong className="text-sm text-neutral-primary">💡 Voorbeelden</strong>
                <p className="text-xs text-neutral-secondaryMid mt-1">
                  Typ @ gevolgd door een gebouw of unit ID voor suggesties
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {Array.from(examplesByScenario.entries()).slice(0, 2).map(([scenario, examples]) => (
                <div key={scenario}>
                  <p className="text-xs font-semibold text-neutral-primary mb-1">{scenario}:</p>
                  <div className="flex flex-wrap gap-1">
                    {examples.slice(0, 3).map((example, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInput(example)}
                        className="text-xs px-2 py-1 bg-white border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-neutral-primary/10 bg-white px-4 py-4">
        <div className="max-w-3xl mx-auto relative">
          {/* Building mentions dropdown */}
          {showMentionDropdown && (
            <div className="absolute bottom-full mb-2 w-full bg-white border border-neutral-primary/20 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
              {mentionSuggestions.length === 0 && (
                <div className="px-4 py-3 text-sm text-neutral-secondaryMid">
                  Zoeken naar gebouwen en units...
                </div>
              )}
              {mentionSuggestions.length > 0 && (
                <>
                  {mentionQuery && (
                    <div className="px-4 py-2 text-xs text-neutral-secondaryMid border-b border-neutral-primary/10">
                      Zoeken naar: <span className="font-semibold text-neutral-primary">{mentionQuery}</span>
                    </div>
                  )}
                  {mentionSuggestions.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => selectMention(item)}
                      className={`w-full px-4 py-2 text-left hover:bg-moss-primary/10 transition-colors flex items-center gap-3 ${
                        index === selectedMentionIndex ? 'bg-moss-primary/10' : ''
                      }`}
                    >
                      {item.type === 'building' ? (
                        <Buildings className="w-4 h-4 text-moss-primary flex-shrink-0" weight="fill" />
                      ) : (
                        <Cube className="w-4 h-4 text-brick-primary flex-shrink-0" weight="fill" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-neutral-primary truncate flex items-center gap-2">
                          {item.id}
                          {item.type === 'unit' && (
                            <span className="text-xs bg-brick-primary/10 text-brick-primary px-1.5 py-0.5 rounded">
                              Unit
                            </span>
                          )}
                        </div>
                        {item.type === 'building' && item.companyName && (
                          <div className="text-xs text-moss-primary truncate">
                            {item.companyName}
                          </div>
                        )}
                        {item.type === 'building' && item.googlePlaceLongAddress && (
                          <div className="text-xs text-neutral-secondaryMid truncate">
                            {item.googlePlaceLongAddress}
                          </div>
                        )}
                        {item.type === 'unit' && item.boxNumber && (
                          <div className="text-xs text-brick-primary truncate">
                            Box: {item.boxNumber}
                          </div>
                        )}
                        {item.type === 'unit' && item.buildingId && (
                          <div className="text-xs text-neutral-secondaryMid truncate">
                            Gebouw: {item.buildingId}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Input field */}
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              className="flex-1 border border-neutral-primary/20 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-moss-primary/20 max-h-32"
              placeholder="Stel een vraag aan Ella... (Gebruik @ voor gebouw/unit suggesties, Enter om te versturen)"
              value={input}
              rows={1}
              onChange={(e) => {
                const newValue = e.target.value;
                const cursor = e.target.selectionStart || 0;
                setInput(newValue);
                checkForMentions(newValue, cursor);

                // Auto-resize textarea
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
              }}
              onKeyDown={handleKeyDown}
              onKeyUp={(e) => {
                const currentValue = e.currentTarget.value;
                const cursor = e.currentTarget.selectionStart || 0;
                checkForMentions(currentValue, cursor);
              }}
              onClick={(e) => {
                const currentValue = e.currentTarget.value;
                const cursor = e.currentTarget.selectionStart || 0;
                checkForMentions(currentValue, cursor);
              }}
              disabled={isProcessing}
            />
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isProcessing}
              className="px-4 py-3 rounded-lg bg-moss-primary text-white hover:bg-moss-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <PaperPlaneRight className="w-5 h-5" weight="fill" />
            </button>
          </div>

          <p className="text-xs text-neutral-secondaryMid mt-2 text-center">
            Druk op Enter om te versturen, Shift+Enter voor een nieuwe regel
          </p>
        </div>
      </div>
    </div>
  );
};
